import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatTableModule } from '@angular/material/table';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgForOf, NgIf, TitleCasePipe } from '@angular/common';
import { takeUntil, tap } from 'rxjs/operators';
import { RefDataService } from '../../../../../../../../services/ref-data.service';
import { Subject } from 'rxjs';
import { LocalizedKeyValue } from '../../../../../../../../shared/models/dataref.model';
import { VariantFormGroupModel } from '../../variant-step-instructions/variant-step-instructions.component';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Utils } from '../../../../../../../../shared/utils/utils';

interface Option {
  value: string;
  label: string;
}

interface NutritionalInformation {
  name: string;
  translationKey: string;
  type: 'select' | 'number' | 'text';
  options?: Option[];
  autoValue: string | number | null | undefined;
  userValue: string | number | null | undefined;
  disabled: boolean;
  min?: number;
  max?: number;
  pattern?: string;
}

@Component({
  selector: 'app-nutritional-information',
  templateUrl: './nutritional-information.component.html',
  styleUrls: ['./nutritional-information.component.scss'],
  imports: [
    TranslateModule,
    MatTableModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    NgIf,
    NgForOf,
    ReactiveFormsModule,
    TitleCasePipe,
  ],
})
export class NutritionalInformationComponent implements OnInit, OnDestroy {
  @Input() formVariant: FormGroup<VariantFormGroupModel>;

  displayedColumns: string[] = ['name', 'autoValue', 'userValue'];
  dataSource: NutritionalInformation[] = [];
  nutritionalIndicators: LocalizedKeyValue[];
  options: Option[] = [];
  initialNutritionValue: any;
  private destroy$ = new Subject<void>();

  constructor(private refDataService: RefDataService) {}

  ngOnInit(): void {
    this.refDataService
      .getByKey('nutritionalIndicators', true)
      .pipe(
        tap((nutritionalIndicators) => {
          this.nutritionalIndicators = this.refDataService.translateReference(this.refDataService.toArray(nutritionalIndicators));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.populateDataSource();
        this.updateDataSourceOptions();
      });
    if (this.formVariant && this.formVariant.get('nutrition')) {
      this.initialNutritionValue = this.formVariant.get('nutrition')?.value;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private populateDataSource(): void {
    this.dataSource = [
      {
        name: 'nutritionalIndicator',
        translationKey: 'NUTRITION.NUTRITIONAL_INDICATOR',
        type: 'select',
        autoValue: this.formVariant.get('detectedNutrition')?.value.nutritionalIndicator ?? '',
        userValue: this.formVariant.get('nutrition')?.value.nutritionalIndicator ?? '',
        disabled: true,
      },
      {
        name: 'nutritionalScore',
        translationKey: 'NUTRITION.NUTRITIONAL_SCORE',
        type: 'number',
        autoValue: this.formVariant.get('detectedNutrition')?.value.nutritionalScore ?? '',
        userValue: this.formVariant.get('nutrition')?.value.nutritionalScore ?? '',
        min: -15,
        max: 40,
        disabled: true,
      },
      {
        name: 'energyValue',
        translationKey: 'NUTRITION.ENERGY_VALUE',
        type: 'number',
        autoValue: this.formVariant.get('detectedNutrition')?.value.detectedEnergyValue ?? '',
        userValue: this.formVariant.get('nutrition')?.value.energyValue ?? '',
        min: 0,
        disabled: true,
      },
      {
        name: 'weight',
        translationKey: 'NUTRITION.WEIGHT',
        type: 'number',
        autoValue: this.formVariant.get('detectedNutrition')?.value.weight ?? '',
        userValue: this.formVariant.get('nutrition')?.value.weight ?? '',
        min: 0,
        disabled: true,
      },
    ];
    this.listenToNutritionalCtrlChange();
  }

  private updateDataSourceOptions(): void {
    if (this.nutritionalIndicators) {
      const options: Option[] = this.nutritionalIndicators
        .map((indicator: LocalizedKeyValue) => {
          const key = indicator.key;
          const label = indicator.name?.GS_FR?.fr;
          return { value: key, label: label || key };
        })
        .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));

      const dataSourceItem = this.dataSource.find((item) => item.name === 'nutritionalIndicator');
      if (dataSourceItem) {
        dataSourceItem.options = options;
      }
    }
  }

  private listenToNutritionalCtrlChange(): void {
    this.formVariant
      ?.get('nutrition')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.addOrRemoveNutritionalInfo();
        this.checkIfFormIsResetToInitial();
      });
  }

  private addOrRemoveNutritionalInfo(): void {
    if (this.formVariant && this.formVariant.get('nutrition')) {
      const nutritionCtrl = this.formVariant.get('nutrition') as FormControl;
      const nutritionValue = nutritionCtrl.value;

      this.dataSource.forEach((item) => {
        if (item.name in nutritionValue) {
          item.userValue = nutritionValue[item.name];
          nutritionValue.areNutritionalIndicatorManuallyModified = true;
        }
      });
    }
  }

  private checkIfFormIsResetToInitial(): void {
    const currentNutritionValue = Utils.sortObjectKeys(this.formVariant?.get('nutrition')?.value);
    const initialNutritionValue = Utils.sortObjectKeys(this.initialNutritionValue);

    if (JSON.stringify(currentNutritionValue) === JSON.stringify(initialNutritionValue)) {
      this.formVariant.markAsPristine();
    }
  }
}
